/**
 * TrustGrid Findings Details - Detailed findings table with filters
 * Shows finding records with context-aware dropdown filters
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Tag, Button, Modal, Tooltip, Badge, Space, Alert, Select, Card, Typography } from 'antd';
import { CheckCircleOutlined, HistoryOutlined } from '@ant-design/icons';
import { useTrustGrid } from '../../../contexts/TrustGridContext';
import TrustGridPage from '../../../components/layout/TrustGridPage';
import ProjectSelector from './ProjectSelector';
import { TGDataTable, LoadingState } from '../../../components/common';
import EventTable from './EventTable';
import SetFindingDispositionModal from './SetFindingDispositionModal';
import FindingDispositionHistoryModal from './FindingDispositionHistoryModal';
import { showError } from '../../../utils/notifications';
import { colors, spacing } from '../../../theme/designTokens';
import { handleCrossOrgError } from '../utils/errorHandlers';
import { getEventsByIds } from '../api/trustgridApi';
import axios from 'axios';

// Control-specific dashboards
import PIIDataQualityDashboard from './PIIDataQualityDashboard';
import CTRL_02_Dashboard from './CTRL_02_Dashboard';
import CTRL_03_Dashboard from './CTRL_03_Dashboard';
import CTRL_04_Dashboard from './CTRL_04_Dashboard';
import CTRL_05_Dashboard from './CTRL_05_Dashboard';
import CTRL_06_Dashboard from './CTRL_06_Dashboard';
import CTRL_07_FairnessDashboard from './CTRL_07_FairnessDashboard';
import CTRL_08_Dashboard from './CTRL_08_Dashboard';
import CTRL_09_Dashboard from './CTRL_09_Dashboard';
import CTRL_10_Dashboard from './CTRL_10_Dashboard';
import CTRL_12_Dashboard from './CTRL_12_Dashboard';
import CTRL_13_Dashboard from './CTRL_13_Dashboard';
import CTRL_14_Dashboard from './CTRL_14_Dashboard';

const { Option } = Select;
const { Text } = Typography;

function FindingsDetails() {
  const { selectedProjectId, selectProject } = useTrustGrid();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const breadcrumbs = useMemo(() => {
    const baseBreadcrumbs = location.state?.breadcrumbs || [
      { label: 'TrustGrid', path: '/trustgrid/dashboard' },
      { label: 'Findings', path: '/trustgrid/findings' },
    ];
    return [...baseBreadcrumbs, { label: 'Details', path: '/trustgrid/findings/details' }];
  }, [location.state]);

  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [controls, setControls] = useState([]);

  // Dropdown filter states (context-aware from URL params)
  const [selectedControl, setSelectedControl] = useState(searchParams.get('control_code') || 'all');
  const [selectedStatus, setSelectedStatus] = useState(searchParams.get('status') || 'all');

  // Evidence modal state
  const [evidenceEvents, setEvidenceEvents] = useState([]);
  const [loadingEvidence, setLoadingEvidence] = useState(false);
  const [evidenceModalVisible, setEvidenceModalVisible] = useState(false);
  const [selectedSignal, setSelectedSignal] = useState(null);

  // Bulk selection state
  const [selectedRowKeys, setSelectedRowKeys] = useState([]); // Signal IDs for table selection
  const [selectedFindingIds, setSelectedFindingIds] = useState([]); // Finding IDs for modal
  const [decisionModalVisible, setDecisionModalVisible] = useState(false);

  // Decision history modal state
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [historyFindingId, setHistoryFindingId] = useState(null);

  // Fetch enabled controls for dropdown
  useEffect(() => {
    if (selectedProjectId) {
      fetchControls();
    }
  }, [selectedProjectId]);

  // Fetch findings when project or filters change
  useEffect(() => {
    if (selectedProjectId) {
      fetchFindings();
    }
  }, [selectedProjectId, selectedControl, selectedStatus]);

  const fetchControls = async () => {
    try {
      const response = await axios.get(`/api/trustgrid/v1/projects/${selectedProjectId}/controls/enabled`);
      setControls(response.data.controls || []);
    } catch (error) {
      console.error('Failed to fetch controls:', error);
    }
  };

  const fetchFindings = async () => {
    if (!selectedProjectId) {
      showError('Please select a project to view findings');
      return;
    }

    setLoading(true);
    try {
      const queryParams = new URLSearchParams();

      if (selectedControl && selectedControl !== 'all') {
        queryParams.append('control_code', selectedControl);
      }

      if (selectedStatus && selectedStatus !== 'all') {
        queryParams.append('status', selectedStatus);
      }

      const response = await axios.get(`/api/trustgrid/v1/projects/${selectedProjectId}/findings/details?${queryParams.toString()}`);
      setFindings(response.data.findings || []);
    } catch (error) {
      if (error.response?.status === 404) {
        handleCrossOrgError(error, selectProject, navigate, 'FindingsDetails');
      } else {
        showError('Failed to load findings');
      }
      console.error('Failed to load findings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleControlChange = (value) => {
    setSelectedControl(value);
    // Update URL params
    const newParams = new URLSearchParams(searchParams);
    if (value === 'all') {
      newParams.delete('control_code');
    } else {
      newParams.set('control_code', value);
    }
    setSearchParams(newParams);
  };

  const handleStatusChange = (value) => {
    setSelectedStatus(value);
    // Update URL params
    const newParams = new URLSearchParams(searchParams);
    if (value === 'all') {
      newParams.delete('status');
    } else {
      newParams.set('status', value);
    }
    setSearchParams(newParams);
  };

  /**
   * Handle view evidence (same logic as Scans.js)
   */
  const handleViewEvidence = async (signal) => {
    setSelectedSignal(signal);
    setEvidenceModalVisible(true);
    setLoadingEvidence(true);

    try {
      const refs = signal.evidence_refs;
      let eventIds = [];

      if (refs) {
        try {
          eventIds = typeof refs === 'string' ? JSON.parse(refs) : refs;
        } catch {
          eventIds = [];
        }
      }

      if (eventIds.length === 0) {
        setEvidenceEvents([]);
        setLoadingEvidence(false);
        return;
      }

      const response = await getEventsByIds(eventIds);
      setEvidenceEvents(response.data.events || []);
    } catch (error) {
      console.error('Failed to fetch evidence events:', error);
      showError(error);
      setEvidenceEvents([]);
    } finally {
      setLoadingEvidence(false);
    }
  };

  const fetchAllEvidence = async () => {
    return evidenceEvents;
  };

  const handleCloseEvidenceModal = () => {
    setEvidenceModalVisible(false);
    setSelectedSignal(null);
    setEvidenceEvents([]);
  };

  // Render control-specific dashboard
  const renderControlDashboard = () => {
    // Only show dashboard when a specific control is selected (not 'all')
    if (!selectedControl || selectedControl === 'all' || !selectedProjectId) {
      return null;
    }

    const dashboardProps = { projectId: selectedProjectId };

    switch (selectedControl) {
      case 'CTRL_01':
        return (
          <Card style={{ marginTop: spacing.lg }}>
            <div style={{ padding: spacing.md }}>
              <h3 style={{ marginBottom: spacing.md }}>Data Quality Dashboard</h3>
              <PIIDataQualityDashboard {...dashboardProps} />
            </div>
          </Card>
        );
      case 'CTRL_02':
        return (
          <Card style={{ marginTop: spacing.lg }}>
            <div style={{ padding: spacing.md }}>
              <h3 style={{ marginBottom: spacing.md }}>Risk Dashboard</h3>
              <CTRL_02_Dashboard {...dashboardProps} />
            </div>
          </Card>
        );
      case 'CTRL_03':
        return (
          <Card style={{ marginTop: spacing.lg }}>
            <div style={{ padding: spacing.md }}>
              <h3 style={{ marginBottom: spacing.md }}>Purpose Dashboard</h3>
              <CTRL_03_Dashboard {...dashboardProps} />
            </div>
          </Card>
        );
      case 'CTRL_04':
        return (
          <Card style={{ marginTop: spacing.lg }}>
            <div style={{ padding: spacing.md }}>
              <h3 style={{ marginBottom: spacing.md }}>Control Dashboard</h3>
              <CTRL_04_Dashboard {...dashboardProps} />
            </div>
          </Card>
        );
      case 'CTRL_05':
        return (
          <Card style={{ marginTop: spacing.lg }}>
            <div style={{ padding: spacing.md }}>
              <h3 style={{ marginBottom: spacing.md }}>Control Dashboard</h3>
              <CTRL_05_Dashboard {...dashboardProps} />
            </div>
          </Card>
        );
      case 'CTRL_06':
        return (
          <Card style={{ marginTop: spacing.lg }}>
            <div style={{ padding: spacing.md }}>
              <h3 style={{ marginBottom: spacing.md }}>Control Dashboard</h3>
              <CTRL_06_Dashboard {...dashboardProps} />
            </div>
          </Card>
        );
      case 'CTRL_07':
        return (
          <Card style={{ marginTop: spacing.lg }}>
            <div style={{ padding: spacing.md }}>
              <h3 style={{ marginBottom: spacing.md }}>Fairness Dashboard</h3>
              <CTRL_07_FairnessDashboard {...dashboardProps} />
            </div>
          </Card>
        );
      case 'CTRL_08':
        return (
          <Card style={{ marginTop: spacing.lg }}>
            <div style={{ padding: spacing.md }}>
              <h3 style={{ marginBottom: spacing.md }}>Control Dashboard</h3>
              <CTRL_08_Dashboard {...dashboardProps} />
            </div>
          </Card>
        );
      case 'CTRL_09':
        return (
          <Card style={{ marginTop: spacing.lg }}>
            <div style={{ padding: spacing.md }}>
              <h3 style={{ marginBottom: spacing.md }}>Control Dashboard</h3>
              <CTRL_09_Dashboard {...dashboardProps} />
            </div>
          </Card>
        );
      case 'CTRL_10':
        return (
          <Card style={{ marginTop: spacing.lg }}>
            <div style={{ padding: spacing.md }}>
              <h3 style={{ marginBottom: spacing.md }}>Control Dashboard</h3>
              <CTRL_10_Dashboard {...dashboardProps} />
            </div>
          </Card>
        );
      case 'CTRL_12':
        return (
          <Card style={{ marginTop: spacing.lg }}>
            <div style={{ padding: spacing.md }}>
              <h3 style={{ marginBottom: spacing.md }}>Control Dashboard</h3>
              <CTRL_12_Dashboard {...dashboardProps} />
            </div>
          </Card>
        );
      case 'CTRL_13':
        return (
          <Card style={{ marginTop: spacing.lg }}>
            <div style={{ padding: spacing.md }}>
              <h3 style={{ marginBottom: spacing.md }}>Control Dashboard</h3>
              <CTRL_13_Dashboard {...dashboardProps} />
            </div>
          </Card>
        );
      case 'CTRL_14':
        return (
          <Card style={{ marginTop: spacing.lg }}>
            <div style={{ padding: spacing.md }}>
              <h3 style={{ marginBottom: spacing.md }}>Control Dashboard</h3>
              <CTRL_14_Dashboard {...dashboardProps} />
            </div>
          </Card>
        );
      default:
        return null;
    }
  };

  // Findings Details columns
  const columns = [
    {
      key: 'control_code',
      title: 'Control',
      dataIndex: 'control_code',
      width: 180,
      fixed: 'left',
      sortable: true,
    },
    {
      key: 'status',
      title: 'Status',
      dataIndex: 'status',
      width: 130,
      render: (status) => {
        const colorMap = {
          open: colors.status.fail,
          in_progress: colors.status.warning,
          closed: colors.status.pass
        };
        return (
          <Tag color={colorMap[status] || 'default'}>
            {status?.replace('_', ' ').toUpperCase()}
          </Tag>
        );
      },
      sortable: true,
    },
    {
      key: 'finding_type',
      title: 'Type',
      dataIndex: 'finding_type',
      width: 100,
      render: (type) => {
        const color = type === 'failure' ? colors.status.fail : colors.status.warning;
        return <Tag color={color}>{type?.toUpperCase()}</Tag>;
      },
      sortable: true,
    },
    {
      key: 'evidence_count',
      title: 'Evidence',
      dataIndex: 'evidence_count',
      width: 100,
      align: 'center',
      render: (count, record) => {
        const actualCount = count || 0;
        if (actualCount === 0) return <span style={{ color: colors.text.secondary }}>0</span>;
        return (
          <Tooltip title={`View ${actualCount} evidence event(s)`}>
            <Button
              type="link"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                const signalData = {
                  control_code: record.control_code,
                  evidence_count: actualCount,
                  evidence_refs: record.evidence_refs,
                };
                handleViewEvidence(signalData);
              }}
              style={{ padding: 0 }}
            >
              <Badge
                count={actualCount}
                showZero={false}
                style={{ backgroundColor: colors.primary[500] }}
              />
            </Button>
          </Tooltip>
        );
      },
      sortable: true,
    },
    {
      key: 'last_scan_date',
      title: 'Last Scan Date',
      dataIndex: 'last_scan_date',
      width: 180,
      render: (date) => date ? new Date(date).toLocaleString() : '—',
      sortable: true,
    },
    {
      key: 'decision',
      title: 'Decision',
      dataIndex: 'decision',
      width: 200,
      render: (decision, record) => {
        if (!decision) return '—';
        return (
          <Space>
            <Tag color="blue">{decision}</Tag>
            {record.finding_id && (
              <Tooltip title="View decision history">
                <Button
                  type="text"
                  size="small"
                  icon={<HistoryOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setHistoryFindingId(record.finding_id);
                    setHistoryModalVisible(true);
                  }}
                />
              </Tooltip>
            )}
          </Space>
        );
      },
      sortable: true,
    },
    {
      key: 'disposition',
      title: 'Disposition',
      dataIndex: 'disposition',
      width: 150,
      render: (disposition) => disposition || '—',
      sortable: true,
    },
    {
      key: 'disposition_date',
      title: 'Disposition Date',
      dataIndex: 'disposition_date',
      width: 180,
      render: (date) => date ? new Date(date).toLocaleString() : '—',
      sortable: true,
    },
    {
      key: 'wait_until',
      title: 'Wait Until',
      dataIndex: 'wait_until',
      width: 180,
      render: (date) => date ? new Date(date).toLocaleString() : '—',
      sortable: true,
    },
  ];

  return (
    <TrustGridPage
      title="Findings Details"
      subtitle="Detailed view of findings with context filters"
      breadcrumbs={breadcrumbs}
      showRefresh
      onRefresh={fetchFindings}
    >
      <ProjectSelector />

      {!selectedProjectId ? (
        <div style={{ textAlign: 'center', padding: spacing.xl }}>
          Please select a project to view findings
        </div>
      ) : (
        <>
          {/* Context Filter Bar */}
          <Card style={{ marginBottom: spacing.md }}>
            <Space size="middle">
              <div>
                <label style={{ marginRight: spacing.xs, fontWeight: 500 }}>Control:</label>
                <Select
                  value={selectedControl}
                  onChange={handleControlChange}
                  style={{ width: 350 }}
                  placeholder="Select control"
                >
                  <Option value="all">All Controls</Option>
                  {controls.map(control => (
                    <Option key={control.code} value={control.code}>
                      {control.code} - {control.title}
                    </Option>
                  ))}
                </Select>
              </div>

              <div>
                <label style={{ marginRight: spacing.xs, fontWeight: 500 }}>Finding Status:</label>
                <Select
                  value={selectedStatus}
                  onChange={handleStatusChange}
                  style={{ width: 200 }}
                  placeholder="Select status"
                >
                  <Option value="all">All Statuses</Option>
                  <Option value="open">Open</Option>
                  <Option value="in_progress">In Progress</Option>
                  <Option value="closed">Closed</Option>
                </Select>
              </div>
            </Space>
          </Card>

          {/* Bulk Actions Toolbar */}
          {selectedFindingIds.length > 0 && (
            <Card style={{ marginBottom: spacing.md, backgroundColor: '#e6f7ff', borderColor: '#91d5ff' }}>
              <Space>
                <Text strong>
                  {selectedFindingIds.length} finding(s) selected
                </Text>
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={() => setDecisionModalVisible(true)}
                >
                  Set Disposition
                </Button>
                <Button onClick={() => {
                  setSelectedRowKeys([]);
                  setSelectedFindingIds([]);
                }}>
                  Clear Selection
                </Button>
              </Space>
            </Card>
          )}

          {loading ? (
            <LoadingState type="spinner" />
          ) : (
            <TGDataTable
              data={findings}
              columns={columns}
              rowKey="id"
              loading={loading}
              pagination="client"
              pageSize={20}
              showExport
              showColumnConfig
              showFilters
              exportFilename="findings-details"
              presetKey="trustgrid.findings.details"
              onRefresh={fetchFindings}
              scroll={{ x: 1400 }}
              rowSelection={{
                selectedRowKeys: selectedRowKeys,
                onChange: (selectedKeys, selectedRows) => {
                  // Update both selectedRowKeys (signal IDs) and selectedFindingIds
                  setSelectedRowKeys(selectedKeys);
                  // Map signal IDs to finding IDs, filter out rows without findings
                  const findingIds = selectedRows
                    .filter(row => row.finding_id)
                    .map(row => row.finding_id);
                  setSelectedFindingIds(findingIds);
                },
                getCheckboxProps: (record) => ({
                  disabled: !record.finding_id, // Disable selection for signals without findings
                }),
                preserveSelectedRowKeys: true,
              }}
            />
          )}

          {/* Control-Specific Dashboard */}
          {renderControlDashboard()}

          {/* Evidence Events Modal (same as Scans.js) */}
          <Modal
            title={
              selectedSignal ? (
                <Space>
                  <span>Evidence for {selectedSignal.control_code}</span>
                  <span style={{ fontSize: 14, fontWeight: 'normal', color: '#888' }}>
                    ({evidenceEvents.length} of {selectedSignal.evidence_count} events)
                  </span>
                </Space>
              ) : (
                'Evidence Events'
              )
            }
            open={evidenceModalVisible}
            onCancel={handleCloseEvidenceModal}
            footer={null}
            width={1400}
            destroyOnClose
          >
            {selectedSignal && evidenceEvents.length < selectedSignal.evidence_count && (
              <Alert
                message="Limited Evidence Available"
                description={`Showing ${evidenceEvents.length} of ${selectedSignal.evidence_count} total events. This scan was created with a system limit on stored evidence references. Re-run the scan to capture all event references for complete traceability.`}
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                closable
              />
            )}
            <EventTable
              data={evidenceEvents}
              loading={loadingEvidence}
              fetchAll={fetchAllEvidence}
            />
          </Modal>

          {/* Set Disposition Modal */}
          <SetFindingDispositionModal
            visible={decisionModalVisible}
            findingIds={selectedFindingIds}
            onClose={() => setDecisionModalVisible(false)}
            onSuccess={() => {
              setSelectedRowKeys([]);
              setSelectedFindingIds([]);
              fetchFindings();
            }}
          />

          {/* Disposition History Modal */}
          <FindingDispositionHistoryModal
            visible={historyModalVisible}
            findingId={historyFindingId}
            onClose={() => {
              setHistoryModalVisible(false);
              setHistoryFindingId(null);
            }}
          />
        </>
      )}
    </TrustGridPage>
  );
}

export default FindingsDetails;
